/**
 * @module services/zotero/exportService
 * @description Export and bibliography functionality for Zotero API
 */

import { apiClient } from './apiClient';
import type { LibraryType, ServiceResult } from '@types';
import { buildLibraryEndpoint } from '@types';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Export formats supported by Zotero API
 */
export const EXPORT_FORMATS = [
  'bibtex',
  'biblatex',
  'bookmarks',
  'coins',
  'csljson',
  'mods',
  'refer',
  'rdf_bibliontology',
  'rdf_dc',
  'rdf_zotero',
  'ris',
  'tei',
  'wikipedia',
] as const;

export type ExportFormat = (typeof EXPORT_FORMATS)[number];

/**
 * Popular CSL citation styles
 */
export const CITATION_STYLES = [
  { id: 'apa', name: 'APA (7th edition)' },
  { id: 'chicago-author-date', name: 'Chicago (Author-Date)' },
  { id: 'chicago-note-bibliography', name: 'Chicago (Note)' },
  { id: 'mla', name: 'MLA (9th edition)' },
  { id: 'harvard-cite-them-right', name: 'Harvard' },
  { id: 'ieee', name: 'IEEE' },
  { id: 'vancouver', name: 'Vancouver' },
  { id: 'nature', name: 'Nature' },
  { id: 'cell', name: 'Cell' },
  { id: 'elsevier-harvard', name: 'Elsevier Harvard' },
] as const;

// =============================================================================
// EXPORT SERVICE
// =============================================================================

/**
 * Export a single item in specified format
 * Uses Zotero's native export via format parameter
 */
export async function exportItem(
  libraryId: number,
  libraryType: LibraryType,
  itemKey: string,
  format: ExportFormat
): Promise<ServiceResult<string>> {
  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);

    const response = await apiClient.requestExport({
      method: 'GET',
      path: `${endpoint}/items/${itemKey}`,
      params: { format },
    });

    return { success: true, data: response };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to export item',
    };
  }
}

/**
 * Export multiple items in specified format
 * Up to 50 items can be exported at once
 */
export async function exportItems(
  libraryId: number,
  libraryType: LibraryType,
  itemKeys: string[],
  format: ExportFormat
): Promise<ServiceResult<string>> {
  if (itemKeys.length === 0) {
    return { success: false, error: 'No items to export' };
  }

  if (itemKeys.length > 50) {
    return { success: false, error: 'Maximum 50 items can be exported at once' };
  }

  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);

    const response = await apiClient.requestExport({
      method: 'GET',
      path: `${endpoint}/items`,
      params: {
        itemKey: itemKeys.join(','),
        format,
      },
    });

    return { success: true, data: response };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to export items',
    };
  }
}

/**
 * Get formatted bibliography for items
 * Uses format=bib with optional style parameter
 */
export async function getBibliography(
  libraryId: number,
  libraryType: LibraryType,
  itemKeys: string[],
  style: string = 'apa',
  locale: string = 'en-US',
  linkwrap: boolean = false
): Promise<ServiceResult<string>> {
  if (itemKeys.length === 0) {
    return { success: false, error: 'No items for bibliography' };
  }

  if (itemKeys.length > 50) {
    return { success: false, error: 'Maximum 50 items for bibliography' };
  }

  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);

    const response = await apiClient.requestExport({
      method: 'GET',
      path: `${endpoint}/items`,
      params: {
        itemKey: itemKeys.join(','),
        format: 'bib',
        style,
        locale,
        linkwrap: linkwrap ? '1' : undefined,
      },
    });

    return { success: true, data: response };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to generate bibliography',
    };
  }
}

/**
 * Export all items in a collection
 */
export async function exportCollection(
  libraryId: number,
  libraryType: LibraryType,
  collectionKey: string,
  format: ExportFormat
): Promise<ServiceResult<string>> {
  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);

    const response = await apiClient.requestExport({
      method: 'GET',
      path: `${endpoint}/collections/${collectionKey}/items`,
      params: {
        format,
        limit: 100, // Export up to 100 items from collection
      },
    });

    return { success: true, data: response };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to export collection',
    };
  }
}
